import React, { useEffect, useRef, useState } from 'react';
import { Volume2, VolumeX } from 'lucide-react';
import { playText, stopText } from '../utils/voicePlayback';

/**
 * Short UI chirp via the Web Audio API. Mirrors the start/stop tone pattern
 * the mic uses, so the user gets the same audible "on/off" confirmation when
 * read-aloud starts and ends. Rising chirp on start (questioning), falling
 * chirp on stop (resolved).
 */
function playUiTone(kind) {
  if (typeof window === 'undefined') return;
  const AC = window.AudioContext || window.webkitAudioContext;
  if (!AC) return;
  try {
    const ctx = new AC();
    const osc = ctx.createOscillator();
    const gain = ctx.createGain();
    osc.type = 'sine';
    const start = ctx.currentTime;
    const dur = 0.14;
    if (kind === 'start') {
      osc.frequency.setValueAtTime(660, start);
      osc.frequency.linearRampToValueAtTime(990, start + dur);
    } else {
      osc.frequency.setValueAtTime(660, start);
      osc.frequency.linearRampToValueAtTime(440, start + dur);
    }
    gain.gain.setValueAtTime(0.0001, start);
    gain.gain.exponentialRampToValueAtTime(0.18, start + 0.01);
    gain.gain.exponentialRampToValueAtTime(0.0001, start + dur);
    osc.connect(gain).connect(ctx.destination);
    osc.start(start);
    osc.stop(start + dur + 0.02);
    osc.onended = () => { try { ctx.close(); } catch (_) { /* noop */ } };
  } catch (_) { /* noop */ }
}

/**
 * Read-aloud button for assistant output.
 *
 * Delegates to the shared voicePlayback utility (which posts to
 * /api/voice/tts → OpenAI gpt-4o-mini-tts and plays the MP3 via an <audio>
 * element). On any failure (network, codec, autoplay block) we fall back to
 * the browser SpeechSynthesis API so the button always does something.
 *
 * Each click logs `[SpeakButton] OpenAI TTS playing` or `[SpeakButton]
 * falling back to Web Speech: <reason>` to the console so you can verify in
 * DevTools which path actually ran.
 *
 * Props:
 *   getText() — getter for the text to read (so streaming responses get the
 *               latest content at click time).
 *   lang      — BCP47 tag for the Web Speech fallback (default 'en-US').
 *   className — passes through to the button.
 */

// ---------- Web Speech fallback voice picker ----------
function pickBestVoice(voices, lang) {
  if (!voices || !voices.length) return null;
  const targetMain = (lang || 'en-US').split('-')[0].toLowerCase();
  const targetFull = (lang || 'en-US').toLowerCase();
  const keywordScore = (name) => {
    const n = (name || '').toLowerCase();
    let s = 0;
    if (n.includes('natural')) s += 6;
    if (n.includes('neural')) s += 6;
    if (n.includes('online')) s += 4;
    if (n.includes('premium')) s += 5;
    if (n.includes('enhanced')) s += 4;
    if (n.startsWith('google ')) s += 5;
    if (n.includes('microsoft')) s += 2;
    if (n.includes('siri')) s += 3;
    if (n.includes('eloquence') || n.includes('compact')) s -= 4;
    return s;
  };
  const localeScore = (v) => {
    const vl = (v.lang || '').toLowerCase();
    if (vl === targetFull) return 4;
    if (vl.startsWith(targetMain + '-')) return 2;
    if (vl.startsWith(targetMain)) return 1;
    return 0;
  };
  const scored = voices.map((v) => ({ v, score: localeScore(v) + keywordScore(v.name) }));
  scored.sort((a, b) => b.score - a.score);
  return scored[0].v;
}

function speakWithWebSpeech(text, lang, voice, onEnd) {
  if (typeof window === 'undefined' || !('speechSynthesis' in window)) {
    onEnd();
    return null;
  }
  const utter = new window.SpeechSynthesisUtterance(text);
  utter.lang = lang;
  if (voice) utter.voice = voice;
  utter.rate = 1.0;
  utter.pitch = 1.05;
  utter.volume = 1.0;
  utter.onend = onEnd;
  utter.onerror = onEnd;
  try {
    window.speechSynthesis.cancel();
    window.speechSynthesis.speak(utter);
  } catch (_) {
    onEnd();
  }
  return utter;
}

export default function SpeakButton({ getText, lang = 'en-US', className = '' }) {
  const [speaking, setSpeaking] = useState(false);
  const fallbackVoiceRef = useRef(null);
  const lastPathRef = useRef(null);    // 'openai' | 'webspeech' — for the title hint
  const utterRef = useRef(null);

  const webSpeechSupported =
    typeof window !== 'undefined' && 'speechSynthesis' in window && 'SpeechSynthesisUtterance' in window;

  // Refresh Web Speech voice list for fallback usage.
  useEffect(() => {
    if (!webSpeechSupported) return undefined;
    const refresh = () => {
      const voices = window.speechSynthesis.getVoices();
      fallbackVoiceRef.current = pickBestVoice(voices, lang);
    };
    refresh();
    window.speechSynthesis.addEventListener?.('voiceschanged', refresh);
    return () => window.speechSynthesis.removeEventListener?.('voiceschanged', refresh);
  }, [webSpeechSupported, lang]);

  // Stop everything on unmount.
  useEffect(
    () => () => {
      stopText();
      try { if (webSpeechSupported) window.speechSynthesis.cancel(); } catch (_) { /* noop */ }
    },
    [webSpeechSupported]
  );

  const stopAll = () => {
    stopText();
    try { if (webSpeechSupported) window.speechSynthesis.cancel(); } catch (_) { /* noop */ }
    setSpeaking(false);
    playUiTone('stop');
  };

  const toggle = async () => {
    if (speaking) {
      stopAll();
      return;
    }
    const text = (typeof getText === 'function' ? getText() : '') || '';
    if (!text.trim()) return;

    // Rising chirp BEFORE we actually start audio so the user has an
    // immediate "yes I heard your click" confirmation even before the
    // network roundtrip for OpenAI TTS lands.
    playUiTone('start');
    setSpeaking(true);

    // Path 1: OpenAI TTS via shared util.
    try {
      await playText(text);
      lastPathRef.current = 'openai';
      console.log(
        '[SpeakButton] OpenAI TTS playing (model + voice configured server-side; defaults are gpt-4o-mini-tts + coral with warm-conversational tone instructions). Override via OPENAI_TTS_VOICE / OPENAI_TTS_INSTRUCTIONS env vars.'
      );
      // playText resolves once playback STARTS. We can't get an exact "ended"
      // signal from the util, so we approximate by text length and fire the
      // stop chirp + reset state when it elapses. Manual stops short-circuit
      // through stopAll() which fires the chirp itself.
      const approxMs = Math.max(2000, Math.min(60000, Math.ceil(text.length / 12) * 1000));
      window.setTimeout(() => {
        setSpeaking(false);
        playUiTone('stop');
      }, approxMs);
      return;
    } catch (err) {
      if (err?.name === 'AbortError') {
        setSpeaking(false);
        return;
      }
      console.warn('[SpeakButton] falling back to Web Speech because OpenAI TTS failed:', err?.message || err);
    }

    // Path 2: Web Speech fallback.
    if (!webSpeechSupported) {
      setSpeaking(false);
      playUiTone('stop');
      return;
    }
    const plain = text
      .replace(/`{1,3}[^`]*`{1,3}/g, ' ')
      .replace(/[*_#>~|]+/g, ' ')
      .replace(/\[([^\]]+)\]\([^)]*\)/g, '$1')
      .replace(/\s+/g, ' ')
      .trim();
    lastPathRef.current = 'webspeech';
    utterRef.current = speakWithWebSpeech(plain, lang, fallbackVoiceRef.current, () => {
      setSpeaking(false);
      playUiTone('stop');
    });
  };

  if (!webSpeechSupported && typeof window !== 'undefined' && !('AudioContext' in window)) {
    return (
      <button type="button" disabled title="Text-to-speech not supported" className={`text-slate-400 cursor-not-allowed ${className}`}>
        <VolumeX size={16} />
      </button>
    );
  }

  const title = speaking
    ? 'Stop reading'
    : lastPathRef.current === 'webspeech'
      ? 'Read aloud (Web Speech — last attempt fell back)'
      : 'Read aloud (OpenAI)';

  return (
    <button
      type="button"
      onClick={toggle}
      aria-pressed={speaking}
      title={title}
      className={`transition ${speaking ? 'text-indigo-700' : 'text-slate-500 hover:text-slate-800'} ${className}`}
    >
      <Volume2 size={16} className={speaking ? 'animate-pulse' : ''} />
    </button>
  );
}
